import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type {
  PrimaryFilterConfig,
  TabContent,
} from '../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';

const companyCompositionSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 8, y: 0, height: 8 },
    id: 'company-composition-snapshot-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 8, width: 8, y: 0, height: 8 },
    id: 'company-composition-snapshot-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 16, width: 8, y: 0, height: 8 },
    id: 'company-composition-snapshot-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 24, width: 8, y: 0, height: 8 },
    id: 'company-composition-snapshot-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 32, width: 8, y: 0, height: 8 },
    id: 'company-composition-snapshot-tenure',
    title: 'Tenure',
    info: {
      text: 'The average amount of time that workers have spent in their position.',
    },
    plot: {
      defaultMetricOptions: ['tenure'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 40, width: 8, y: 0, height: 8 },
    id: 'company-composition-snapshot-salary',
    title: 'Salary',
    info: {
      text: 'Our estimate of average salary. More information on the salary model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['salary'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 0, width: 16, y: 8, height: 14 },
    id: 'company-composition-snapshot-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 16, width: 16, y: 8, height: 14 },
    id: 'company-composition-snapshot-geography',
    title: 'Geography',
    info: {
      text: 'The composition of workers by geography.',
    },
    plot: {
      secondaryDimension: 'geography',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 32, width: 16, y: 8, height: 14 },
    id: 'company-composition-snapshot-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      hideOther: true,
    },
  },
  {
    position: { x: 0, width: 16, y: 22, height: 14 },
    id: 'company-composition-snapshot-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 16, width: 16, y: 22, height: 14 },
    id: 'company-composition-snapshot-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 32, width: 16, y: 22, height: 14 },
    id: 'company-composition-snapshot-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
];

const companyCompositionOvertimeItems: TabContent['items'] = [
  {
    position: { x: 0, width: 8, y: 0, height: 8 },
    id: 'company-composition-overtime-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
    },
  },
  {
    position: { x: 8, width: 8, y: 0, height: 8 },
    id: 'company-composition-overtime-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 8, y: 0, height: 8 },
    id: 'company-composition-overtime-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 24, width: 8, y: 0, height: 8 },
    id: 'company-composition-overtime-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 8, y: 0, height: 8 },
    id: 'company-composition-overtime-tenure',
    title: 'Tenure',
    info: {
      text: 'The average amount of time that workers have spent in their position.',
    },
    plot: {
      defaultMetricOptions: ['tenure'],
      chartType: 'line',
    },
  },
  {
    position: { x: 40, width: 8, y: 0, height: 8 },
    id: 'company-composition-overtime-salary',
    title: 'Salary',
    info: {
      text: 'Our estimate of average salary. More information on the salary model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['salary'],
      chartType: 'line',
    },
  },
  {
    position: { x: 0, width: 16, y: 8, height: 14 },
    id: 'company-composition-overtime-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 16, width: 16, y: 8, height: 14 },
    id: 'company-composition-overtime-geography',
    title: 'Geography',
    info: {
      text: 'The composition of workers by geography.',
    },
    plot: {
      secondaryDimension: 'geography',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 32, width: 16, y: 8, height: 14 },
    id: 'company-composition-overtime-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 0, width: 16, y: 22, height: 14 },
    id: 'company-composition-overtime-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 16, width: 16, y: 22, height: 14 },
    id: 'company-composition-overtime-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 32, width: 16, y: 22, height: 14 },
    id: 'company-composition-overtime-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
];

const sharedConfig: Pick<TabContent, 'grid'> & {
  primaryFilter: PrimaryFilterConfig;
} = {
  primaryFilter: {
    id: FilterId.COMPANY,
    selectionLimit: 6,
    selectionDefault: 5,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
};

export const companyCompositionSnapshotTab: TabContent = {
  id: 'company-composition-snapshot',
  items: companyCompositionSnapshotItems,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.CONTINGENT_WORKERS,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.KEYWORDS,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.SNAPSHOT_DATE,
    FilterId.TITLE,
  ].map((id) => ({ id })),
};

const companyCompositionOvertimeTab: TabContent = {
  id: 'company-composition-overtime',
  items: companyCompositionOvertimeItems,
  ...sharedConfig,
  primaryFilter: { ...sharedConfig.primaryFilter, tagColors: true },
  secondaryFilters: [
    FilterId.CONTINGENT_WORKERS,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.KEYWORDS,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.TITLE,
  ].map((id) => ({ id })),
};

export const companyComposition: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.COMPANY_COMPOSITION,
  revelioPageId: RevelioPageId.COMPANY_COMPOSITION,
  name: 'Compositions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'company-composition-snapshot',
      label: 'Snapshot',
      isActive: true,
      tabContent: companyCompositionSnapshotTab,
    },
    {
      id: 'company-composition-overtime',
      label: 'Over Time',
      isActive: false,
      tabContent: companyCompositionOvertimeTab,
    },
  ],
};
